import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Coins, ChevronRight, Trophy, Flame, ShieldCheck, ArrowUpRight, ArrowRight, Calendar, Plus, Minus } from "lucide-react";
import TopBar from "@/components/app/TopBar";
import Sidebar from "@/components/app/Sidebar";
import LevelHero from "@/components/profile/LevelHero";
import RedemptionCatalog from "@/components/profile/RedemptionCatalog";
import PhotoGallery from "@/components/profile/PhotoGallery";
import PasswordChangeCard from "@/components/profile/PasswordChangeCard";
import ThemeSelectorCard from "@/components/profile/ThemeSelectorCard";
import MobileAppCard from "@/components/profile/MobileAppCard";
import { ACTION_LABELS, CATEGORY_LABELS } from "@/lib/sparks";
import { fmtNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tsFormat = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleDateString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function Profile() {
  const auth = useAuth();
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);

  const userId = auth?.user && typeof auth.user === "object" ? auth.user.id : null;

  const load = async () => {
    if (!userId) return;
    try {
      const [b, h] = await Promise.all([
        axios.get(`${API}/sparks/balance`, { params: { user_id: userId } }),
        axios.get(`${API}/sparks/history`, { params: { user_id: userId, limit: 30 } }),
      ]);
      setBalance(b.data);
      setHistory(h.data);
    } catch {
      // For brand-new users, sparks balance returns zeros automatically
    }
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const redeem = async (itemId) => {
    try {
      const { data } = await axios.post(`${API}/sparks/redeem`, { user_id: userId, item: itemId });
      setBalance(data);
      toast.success("Recompensa canjeada · revisa tu perfil");
      const { data: h } = await axios.get(`${API}/sparks/history`, { params: { user_id: userId, limit: 30 } });
      setHistory(h);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo canjear");
    }
  };

  const earnDemo = async (action) => {
    try {
      const { data } = await axios.post(`${API}/sparks/earn`, { user_id: userId, action });
      setBalance(data);
      toast.success(`+${data.total_sparks - (balance?.total_sparks || 0)} Sparks · ${ACTION_LABELS[action] || action}`);
      const { data: h } = await axios.get(`${API}/sparks/history`, { params: { user_id: userId, limit: 30 } });
      setHistory(h);
    } catch {
      toast.error("No se pudo registrar la acción");
    }
  };

  // Loading guard while auth resolves
  if (auth?.user === null) {
    return <div className="min-h-screen flex items-center justify-center text-ink-mid font-mono text-xs">Cargando perfil…</div>;
  }
  // Not logged in → redirect to login (preserve intended destination)
  if (!auth?.user || typeof auth.user !== "object") {
    return <Navigate to="/login" state={{ from: "/perfil" }} replace />;
  }

  return (
    <div data-testid="profile-page" className="bg-cream min-h-screen text-ink pb-20">
      <TopBar />
      <div className="max-w-[1600px] mx-auto flex">
        <Sidebar />

        <main className="flex-1 min-w-0 px-4 md:px-6 lg:px-8 py-5">
          <div className="max-w-[1100px] mx-auto">
            {/* Hero */}
            <LevelHero balance={balance} user={auth?.user || null} />

            {/* KPIs */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <KPI testid="kpi-sparks"     icon={Flame}      k={fmtNumber(balance?.total_sparks ?? 0)}   v="Sparks disponibles" highlight />
              <KPI testid="kpi-lifetime"   icon={Trophy}     k={fmtNumber(balance?.lifetime_earned ?? 0)} v="Total ganado en vida" />
              <KPI testid="kpi-redeemed"   icon={ArrowUpRight} k={fmtNumber(balance?.redeemed ?? 0)}     v="Sparks canjeados" />
              <KPI testid="kpi-mult"       icon={ShieldCheck} k={`${balance?.earn_mult ?? 1}×`}          v="Multiplicador actual" />
            </section>

            {/* Quick earn (challenges + demo) */}
            <section data-testid="challenges" className="border hairline bg-cream-card rounded-2xl p-6 md:p-8 mb-8">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral">retos disponibles</p>
                  <h3 className="font-serif text-3xl tracking-tight mt-1">Gana Sparks ahora</h3>
                </div>
                <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
                  Multiplicador {balance?.earn_mult ?? 1}× aplicado
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ChallengeCard testid="ch-easy"   icon={Plus}      title="Conexión diaria"        reward={10}  level="fácil"      onClick={() => earnDemo("daily_login")} />
                <ChallengeCard testid="ch-medium" icon={Calendar}  title="Reto comunitario · 7 días" reward={150} level="medio"   onClick={() => earnDemo("challenge_medium")} />
                <ChallengeCard testid="ch-hard"   icon={Flame}     title="Sube tu primera obra"   reward={500} level="épico"      onClick={() => earnDemo("challenge_hard")} />
              </div>
            </section>

            {/* Photo gallery */}
            <PhotoGallery userId={userId} />

            {/* Security · password change */}
            <PasswordChangeCard />

            {/* Trip Planner CTA */}
            <Link
              to="/mis-planes"
              data-testid="profile-plans-link"
              className="block border hairline bg-gradient-to-br from-coral/10 via-cream-card to-cream rounded-2xl p-5 hover:shadow-lg hover:border-coral transition group"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-coral/15 grid place-items-center shrink-0">
                  <Calendar className="w-5 h-5 text-coral" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-coral">◊ Nuevo</p>
                  <h3 className="font-serif text-xl leading-tight">Mis planes de viaje</h3>
                  <p className="text-xs text-ink-mid mt-0.5">
                    Cronograma por día y hora · dónde comer · con IA opcional
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-ink-mid group-hover:text-coral group-hover:translate-x-1 transition" />
              </div>
            </Link>

            {/* Apariencia · theme selector */}
            <ThemeSelectorCard />

            {/* Mobile app */}
            <MobileAppCard />

            {/* Redemption catalog */}
            <RedemptionCatalog balance={balance} onRedeem={redeem} />

            {/* History */}
            <section className="mt-8 border hairline bg-cream-card rounded-2xl p-3 md:p-5">
              <div className="flex items-center justify-between mb-3 px-2">
                <h3 className="font-serif text-2xl tracking-tight">Historial Spark</h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
                  últimos {history.length} eventos
                </span>
              </div>
              <div className="grid grid-cols-12 gap-3 px-3 pb-2 border-b hairline font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
                <div className="col-span-6">acción</div>
                <div className="col-span-3 hidden md:block">fecha</div>
                <div className="col-span-3 md:col-span-3 text-right">cambio</div>
              </div>
              {history.length === 0 ? (
                <p className="px-6 py-12 text-center text-sm text-ink-mid">Aún no hay actividad. Empieza con un reto arriba.</p>
              ) : (
                history.map((ev) => {
                  const isEarn = ev.kind === "earn";
                  return (
                    <div key={ev.id} data-testid={`event-${ev.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 border-b hairline last:border-b-0 items-center">
                      <div className="col-span-6 md:col-span-6 flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${isEarn ? "bg-coral/15 text-coral" : "bg-ink/10 text-ink-mid"}`}>
                          {isEarn ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-ink truncate">{ACTION_LABELS[ev.action] || ev.action}</p>
                          {ev.ref && <p className="text-[11px] text-ink-mid truncate">{ev.ref}</p>}
                        </div>
                      </div>
                      <div className="col-span-0 md:col-span-3 hidden md:block text-xs text-ink-mid font-mono">
                        {tsFormat(ev.created_at)}
                      </div>
                      <div className={`col-span-6 md:col-span-3 text-right text-sm font-mono ${isEarn ? "text-coral" : "text-ink-mid"}`}>
                        {isEarn ? "+" : ""}{ev.amount.toLocaleString("es")} ✦
                      </div>
                    </div>
                  );
                })
              )}
            </section>

            <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
              ✦ Categorías de canje: {Object.values(CATEGORY_LABELS).join(" · ")}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, k, v, highlight, testid }) {
  return (
    <div data-testid={testid} className={`border hairline rounded-2xl p-4 ${highlight ? "bg-ink text-cream border-ink" : "bg-cream-card"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${highlight ? "text-coral" : "text-coral"}`} strokeWidth={1.7} />
        <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${highlight ? "text-cream/70" : "text-ink-mid"}`}>{v}</p>
      </div>
      <p className="font-serif text-3xl tracking-tighter">{k}{highlight && <span className="text-coral text-2xl"> ✦</span>}</p>
    </div>
  );
}

function ChallengeCard({ icon: Icon, title, reward, level, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left border hairline bg-cream-card hover:border-coral hover:shadow-lg transition-all rounded-2xl p-4 flex items-center justify-between gap-3 group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-10 h-10 rounded-xl bg-coral/10 text-coral flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{title}</p>
          <p className="text-[11px] font-mono uppercase tracking-wider text-ink-mid">{level}</p>
        </div>
      </div>
      <div className="text-right shrink-0">
        <span className="text-sm font-mono text-coral">+{reward} ✦</span>
        <ChevronRight className="w-4 h-4 text-ink-mid group-hover:text-coral transition ml-auto" />
      </div>
    </button>
  );
}
